/**
 * Reading the dictation socket's frames, and where it lives.
 *
 * Pure, and apart from `dictation-socket.ts` for the reason `live-wire.ts`
 * gives: the socket reaches for `apiBaseUrl`, which pulls React Native in, and
 * a test cannot load it. The reader is as loose as the assistant call's: a
 * frame this build does not know is null, never a crash.
 */
import type { DictationServerMessage } from '@alluzo/shared';

import { liveWsUrl } from '@/lib/live-wire';

/** The API host over ws, at the dictation route. Built from the live call's URL so the http-to-ws rule lives in one place. */
export const dictationWsUrl = (base: string): string => liveWsUrl(base).replace(/\/live$/, '/dictate/live');

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parsed = (raw: string): unknown => {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
};

/**
 * One frame the phone knows, or null: `open`; `words` and `done` only with a
 * string `text`; `error` only with one of its two codes.
 */
export const readDictationServerMessage = (raw: unknown): DictationServerMessage | null => {
  const body = typeof raw === 'string' ? parsed(raw) : raw;
  if (!isObject(body) || typeof body.type !== 'string') return null;
  switch (body.type) {
    case 'open':
      return { type: 'open' };
    case 'words':
      return typeof body.text === 'string' ? { type: 'words', text: body.text } : null;
    case 'done':
      return typeof body.text === 'string' ? { type: 'done', text: body.text } : null;
    case 'error':
      return body.code === 'DICTATION_UNAVAILABLE' || body.code === 'DICTATION_FAILED' ? { type: 'error', code: body.code } : null;
    default:
      return null;
  }
};
